import { Component, Input, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { environment } from '../../../../environments/environment';
import { TenantId } from '../../../../environments/environments.types';
import { APPLICATION_TIME_ZONE } from '../../../lib/date-time';
import {
  BookingService,
  FormUtilsService,
} from '../../../services';
import {
  PaymentStatus,
  SelectOption,
} from '../../../types';

@Component({
  selector: 'app-change-payment-status-dialog',
  templateUrl:
    './change-payment-status-dialog.component.html',
})
export class ChangePaymentStatusDialogComponent
  implements OnInit
{
  paymentExpectedByControl = new FormControl<
    string | undefined
  >(undefined);
  paymentStatusChangeCommentControl =
    new FormControl<string>('');
  result: { changed: true } | null = null;
  targetPaymentStatusControl = new FormControl<
    PaymentStatus | undefined
  >(undefined, Validators.required);
  targetPaymentStatusOptions!: SelectOption[];

  private incompleteStatuses: PaymentStatus[] = [
    'open',
    'canceled',
    'pending',
    'authorized',
    'expired',
    'failed',
  ];

  @Input() data!: {
    bookingId: number;
    bookingReference: string;
    currentPaymentStatus: PaymentStatus;
  };

  constructor(
    private bookingService: BookingService,
    private formUtils: FormUtilsService,
    public bsModalRef: BsModalRef<ChangePaymentStatusDialogComponent>
  ) {}

  ngOnInit(): void {
    if (
      this.incompleteStatuses.includes(
        this.data.currentPaymentStatus
      )
    ) {
      this.targetPaymentStatusOptions =
        this.paymentStatusesToSelectOptions([
          'open',
          'paid',
          'not_required',
        ]);
    } else {
      // This shouldn't happen
      this.targetPaymentStatusOptions = [];
      console.error(
        `The booking ${this.data.bookingId} is in status ${this.data.currentPaymentStatus}, that cannot be changed.`
      );
    }
  }

  private paymentStatusesToSelectOptions = (
    statuses: PaymentStatus[]
  ): SelectOption[] => {
    return statuses.map((status) => ({
      value: status,
      label: `booking.${this.tenant}.status.${status}`,
    }));
  };

  async changePaymentStatus(): Promise<void> {
    let targetStatus = this.formUtils.unwrapSingleSelect(
      this.targetPaymentStatusControl.value
    );

    if (targetStatus) {
      const comment =
        this.paymentStatusChangeCommentControl.value || '';
      // We only want to send a "payment expected by" date when the target status is 'open'
      const paymentExpectedBy =
        targetStatus === 'open' &&
        this.paymentExpectedByControl.value
          ? DateTime.fromISO(
              this.paymentExpectedByControl.value
            )
          : undefined;
      await this.bookingService.changeBookingPaymentStatus(
        this.data.bookingId,
        targetStatus,
        comment,
        paymentExpectedBy
      );
      this.result = { changed: true };
      this.bsModalRef.hide();
    } else {
      console.error(
        `Could not request changing the payment status of booking ${this.data.bookingId}. No target payment status selected`
      );
      return Promise.reject(
        new Error('No target payment status selected')
      );
    }
  }

  get tenant(): TenantId {
    return environment.tenant;
  }

  get earliestPossiblePayment(): Date {
    return DateTime.now()
      .setZone(APPLICATION_TIME_ZONE)
      .plus({
        minute: 30,
      })
      .toJSDate();
  }

  get latestPossiblePayment(): Date {
    return DateTime.now()
      .setZone(APPLICATION_TIME_ZONE)
      .plus({
        week: 4,
      })
      .toJSDate();
  }
}
